// Source unique de vérité pour les taxonomies et valeurs de taxonomie.
//
// Champs par taxonomy :
//   label     — libellé affiché en UI / logs
//   type      — "multi_value" | "categorical" | "gate" | "value"
//   enrichable — true si la taxonomy est classifiée par le LLM (mission-enrichment)
//   gate        — true si la taxonomy est un filtre dur dans le matching engine
//   transformer — fonction optionnelle qui calcule des value keys depuis des params utilisateur
//
// Champs par valeur :
//   label             — libellé affiché
//   sublabel          — aide contextuelle optionnelle pour les UIs
//   icon              — emoji optionnel
//   enrichable        — false pour les valeurs exclues de l'enrichissement (ex : je_ne_sais_pas)
//   mission_card_tag  — libellé court affiché en tag sur les cartes mission quand la valeur a matché
//   neutral           — true pour exclure la valeur du scoring utilisateur (cf. NEUTRAL_TAXONOMY_VALUE_KEYS)

import { DEPARTMENT_CODE_VALUES, resolveDepartmentCodeValues } from "./transformers/department-code";
import { resolveLocationValues } from "./transformers/location";
import { resolveTrancheAgeValues } from "./transformers/tranche-age";

export const TAXONOMY = {
  // ─── Taxonomies enrichissables ────────────────────────────────────────────

  // Taxonomie historique utilisée par les prompts d'enrichissement v1 à v4, mais plus par
  // le prochain parcours. À conserver sans modifier pour la rétrocompatibilité ; candidate
  // à une suppression lors d'une future migration.
  domaine: {
    label: "Domaine",
    type: "multi_value",
    enrichable: true,
    gate: false,
    values: {
      sante_soins: {
        label: "Santé et soins",
        icon: "🩺",
        sublabel: "Prendre soin des autres au quotidien",
        enrichable: true,
      },
      social_solidarite: {
        label: "Social et solidarité",
        icon: "🤝",
        sublabel: "Accompagner les personnes vulnérables",
        enrichable: true,
      },
      environnement_nature: {
        label: "Environnement et nature",
        icon: "🌿",
        sublabel: "Agir pour la planète et les territoires",
        enrichable: true,
      },
      sport_animation: {
        label: "Sport et animation sportive",
        icon: "⚽",
        sublabel: "Encadrer, motiver, transmettre par le sport",
        enrichable: true,
      },
      culture_arts: {
        label: "Culture et arts",
        icon: "🎨",
        sublabel: "Créer, animer, faire rayonner",
        enrichable: true,
      },
      education_transmission: {
        label: "Éducation et transmission",
        icon: "🎓",
        sublabel: "Apprendre, encadrer, faire grandir",
        enrichable: true,
      },
      securite_defense: {
        label: "Sécurité et défense",
        icon: "🛡️",
        sublabel: "Protéger les personnes et les biens",
        enrichable: true,
      },
      international_humanitaire: {
        label: "International et humanitaire",
        icon: "🌍",
        enrichable: true,
      },
      gestion_projet: {
        label: "Gestion de projet",
        icon: "📋",
        sublabel: "Organiser, coordonner, piloter",
        enrichable: true,
      },
      je_ne_sais_pas: {
        label: "Je ne sais pas encore",
        icon: "🤔",
        sublabel: "Je découvrirai en explorant les missions",
        enrichable: false,
        neutral: true,
      },
    },
  },

  domaine_engagement: {
    label: "Domaine d’engagement",
    type: "multi_value",
    enrichable: true,
    gate: false,
    values: {
      sante_bien_etre: {
        label: "Santé et bien-être",
        icon: "🏥",
        enrichable: true,
      },
      sport: {
        label: "Sport",
        icon: "🏀",
        enrichable: true,
      },
      solidarite_inclusion: {
        label: "Solidarité et inclusion",
        icon: "🍲",
        enrichable: true,
      },
      environnement_animaux: {
        label: "Environnement et animaux",
        icon: "🌱",
        enrichable: true,
      },
      art_culture: {
        label: "Art et culture",
        icon: "🎨",
        enrichable: true,
      },
      securite_secours: {
        label: "Sécurité et secours",
        icon: "🛡️",
        enrichable: true,
      },
      citoyennete: {
        label: "Citoyenneté",
        icon: "🗳️",
        enrichable: true,
      },
      numerique: {
        label: "Numérique",
        icon: "💻",
        enrichable: true,
      },
      education: {
        label: "Éducation",
        icon: "🎓",
        enrichable: true,
      },
    },
  },

  // Taxonomie historique utilisée par les prompts d'enrichissement v1 à v4, mais plus par
  // le prochain parcours. À conserver sans modifier pour la rétrocompatibilité ; candidate
  // à une suppression lors d'une future migration.
  secteur_activite: {
    label: "Secteur d'activité (référentiel ROME)",
    type: "multi_value",
    enrichable: true,
    gate: false,
    values: {
      sante_social_aide_personne: {
        label: "Santé, social et aide à la personne",
        icon: "🏥",
        sublabel: "Soigner, accompagner, soutenir",
        enrichable: true,
      },
      education_formation_animation: {
        label: "Éducation, formation et animation",
        icon: "🎓",
        sublabel: "Transmettre, apprendre, encadrer",
        enrichable: true,
      },
      securite_service_public: {
        label: "Sécurité et service public",
        icon: "🛡️",
        sublabel: "Protéger et servir la collectivité",
        enrichable: true,
      },
      environnement_agriculture: {
        label: "Environnement et agriculture",
        icon: "🌿",
        sublabel: "Préserver la nature et les territoires",
        enrichable: true,
      },
      culture_creation_medias: {
        label: "Culture, création et médias",
        icon: "🎨",
        sublabel: "Créer, diffuser, faire rayonner",
        enrichable: true,
      },
      numerique_communication: {
        label: "Numérique et communication",
        icon: "💻",
        sublabel: "Innover, connecter, informer",
        enrichable: true,
      },
      batiment_industrie_logistique: {
        label: "Bâtiment, industrie et logistique",
        icon: "🏗️",
        sublabel: "Construire, produire, organiser",
        enrichable: true,
      },
      gestion_commerce_organisation: {
        label: "Gestion, commerce et organisation",
        icon: "📊",
        sublabel: "Gérer, coordonner, développer",
        enrichable: true,
      },
      je_ne_sais_pas: {
        label: "Je ne sais pas encore",
        icon: "🤔",
        sublabel: "Je découvrirai en explorant les missions",
        enrichable: false,
        neutral: true,
      },
    },
  },

  // Taxonomie historique utilisée par les prompts d'enrichissement v1 à v4, mais plus par
  // le prochain parcours. À conserver sans modifier pour la rétrocompatibilité ; candidate
  // à une suppression lors d'une future migration.
  type_mission: {
    label: "Type / durée de mission",
    type: "categorical",
    enrichable: true,
    gate: false,
    values: {
      ponctuelle: {
        label: "Mission ponctuelle",
        sublabel: "Quelques heures ou quelques jours, une fois",
        icon: "😇‍",
        enrichable: true,
      },
      reguliere: {
        label: "Mission régulière",
        sublabel: "Quelques heures par semaine ou par mois. Certaines missions peuvent être indemnisées",
        icon: "☺️",
        enrichable: true,
      },
      temps_plein: {
        label: "Mission à temps plein",
        sublabel: "Plusieurs jours par semaine pendant plusieurs mois, les missions sont souvent indemnisées",
        icon: "🤠‍",
        enrichable: true,
      },
      je_ne_sais_pas: {
        label: "Je ne sais pas encore",
        sublabel: "Je déciderai en découvrant les missions",
        icon: "🤔‍",
        enrichable: false,
        neutral: true,
      },
    },
  },

  rythme: {
    label: "Rythme de mission",
    type: "multi_value",
    enrichable: true,
    gate: false,
    values: {
      ponctuelle_journee: {
        label: "Une mission ponctuelle, sur une journée",
        icon: "🔎",
        enrichable: true,
      },
      quelques_heures_semaine: {
        label: "Quelques heures par semaine",
        icon: "👋",
        enrichable: true,
      },
      plusieurs_jours_semaine: {
        label: "Plusieurs jours par semaine",
        icon: "💪",
        enrichable: true,
      },
      quelques_jours_annee: {
        label: "Quelques jours répartis dans l’année",
        icon: "📆",
        enrichable: true,
      },
      temps_plein_plusieurs_mois: {
        label: "À temps plein pendant plusieurs mois",
        icon: "💼",
        enrichable: true,
      },
      je_ne_sais_pas: {
        label: "Je ne sais pas encore",
        icon: "🤔",
        enrichable: false,
        neutral: true,
      },
    },
  },

  // Dispositif d'engagement de la mission. Valeur déterministe injectée depuis les données
  // structurées de la mission (cf. SCORING_RULES côté API), pas enrichie par le LLM.
  dispositif: {
    label: "Dispositif d'engagement",
    type: "categorical",
    enrichable: false,
    gate: false,
    missionDerived: true,
    values: {
      benevolat: { label: "Bénévolat", icon: null, enrichable: false },
      service_civique: { label: "Service civique", icon: null, enrichable: false },
      sapeurs_pompiers: { label: "Pompiers volontaires", icon: null, enrichable: false },
      reserve_gendarmerie: { label: "Réserve Gendarmerie", icon: null, enrichable: false },
      reserve_police_nationale: { label: "Réserve Police Nationale", icon: null, enrichable: false },
      reserve_armees: { label: "Réserves des armées", icon: null, enrichable: false },
    },
  },

  // Taxonomie historique utilisée par les prompts d'enrichissement v1 à v4, mais plus par
  // le prochain parcours. À conserver sans modifier pour la rétrocompatibilité ; candidate
  // à une suppression lors d'une future migration.
  competence_rome: {
    label: "Compétences (référentiel ROME)",
    type: "multi_value",
    enrichable: true,
    gate: false,
    values: {
      management_social_soin: { label: "Management, social, soin", icon: "🤲", enrichable: true },
      communication_creation_numerique: { label: "Communication, création, innovation, nouvelles technologies", icon: "💻", enrichable: true },
      production_construction_qualite_logistique: { label: "Production, construction, qualité, logistique", icon: "🛠️", enrichable: true },
      gestion_pilotage_juridique: { label: "Gestion, pilotage, juridique", icon: "💼", enrichable: true },
      relation_client_commerce_strategie: { label: "Relation client, commerce, stratégie", icon: "📈", enrichable: true },
      cooperation_organisation_soft_skills: { label: "Coopération, organisation, soft skills", icon: "🤝", enrichable: true },
      securite_environnement_action_publique: { label: "Protection des personnes, de la société ou de l'environnement", icon: "🛡️", enrichable: true },
      je_ne_sais_pas: { label: "Autre / Je ne sais pas", icon: "‍‍🤔‍", enrichable: false, neutral: true },
    },
  },

  // Taxonomie historique utilisée par les prompts d'enrichissement v1 à v4, mais plus par
  // le prochain parcours. À conserver sans modifier pour la rétrocompatibilité ; candidate
  // à une suppression lors d'une future migration.
  region_internationale: {
    label: "Région internationale",
    type: "categorical",
    enrichable: true,
    gate: false,
    values: {
      europe: { label: "Europe", icon: null, enrichable: true },
      afrique: { label: "Afrique", icon: null, enrichable: true },
      amerique: { label: "Amérique", icon: null, enrichable: true },
      asie: { label: "Asie", icon: null, enrichable: true },
      je_ne_sais_pas: { label: "Je ne sais pas encore", icon: null, enrichable: false, neutral: true },
    },
  },

  // Taxonomie historique utilisée par les prompts d'enrichissement v1 à v4, mais plus par
  // le prochain parcours. À conserver sans modifier pour la rétrocompatibilité ; candidate
  // à une suppression lors d'une future migration.
  engagement_intent: {
    label: "Intention d'engagement",
    type: "multi_value",
    enrichable: true,
    gate: false,
    values: {
      aide_directe: { label: "Aide directe aux personnes", icon: "🤝", enrichable: true },
      transmission: { label: "Transmission / pédagogie / accompagnement de public", icon: "🎓", enrichable: true },
      animation: { label: "Animation d'actions ou de collectif", icon: "🎉", enrichable: true },
      action_terrain: { label: "Action terrain concrète (collecte, distribution, fabrication…)", icon: "🌱", enrichable: true },
      secours: { label: "Secours / intervention", icon: "🚒", enrichable: true },
      cadre_engage: { label: "Engagement en cadre structuré", icon: "🪖", enrichable: true },
      support_organisation: { label: "Organisation / gestion de projet / communication", icon: "🧠", enrichable: true },
      exploration: { label: "Je ne sais pas encore", icon: "🤷", enrichable: false, neutral: true },
    },
  },

  activite: {
    label: "Type d’activité",
    type: "multi_value",
    enrichable: true,
    gate: false,
    values: {
      aider_accompagner: {
        label: "Aider et accompagner des personnes",
        icon: "❤️",
        enrichable: true,
        mission_card_tag: "Aide aux personnes",
      },
      transmettre_animer: {
        label: "Transmettre et animer",
        icon: "📢",
        enrichable: true,
        mission_card_tag: "Animation et transmission",
      },
      fabriquer_reparer_terrain: {
        label: "Fabriquer, réparer ou agir sur le terrain",
        icon: "🛠️",
        enrichable: true,
        mission_card_tag: "Travail manuel et de terrain",
      },
      secourir_proteger: {
        label: "Secourir et protéger",
        icon: "🛟",
        enrichable: true,
        mission_card_tag: "Secours et protection",
      },
      organiser_coordonner: {
        label: "Organiser et coordonner",
        icon: "🗓️",
        enrichable: true,
        mission_card_tag: "Organisation et coordination",
      },
      creer_communiquer: {
        label: "Créer et communiquer",
        icon: "🎨",
        enrichable: true,
        mission_card_tag: "Ta créativité au service d’une cause",
      },
    },
  },

  // Historique (q2/q3), plus posée par q4 ; à conserver pour la rétrocompatibilité (rollback q3).
  equipe: {
    label: "Cadre d’équipe",
    type: "categorical",
    enrichable: true,
    gate: false,
    values: {
      autonomie: {
        label: "Plutôt en autonomie",
        icon: "🌱",
        enrichable: true,
      },
      petit_groupe: {
        label: "Dans un petit groupe où l’on prend le temps de se connaître",
        icon: "🌿",
        enrichable: true,
        mission_card_tag: "Une équipe de moins de 10 bénévoles",
      },
      grand_collectif: {
        label: "Dans un grand collectif où il y a beaucoup de monde",
        icon: "🌳",
        enrichable: true,
        mission_card_tag: "Une grande équipe engagée",
      },
      peu_importe: {
        label: "Peu importe",
        icon: "🤔",
        enrichable: false,
        neutral: true,
      },
    },
  },

  // Historique (q2), plus posée depuis q3 ; à conserver pour la rétrocompatibilité.
  interaction: {
    label: "Mode d’interaction",
    type: "categorical",
    enrichable: true,
    gate: false,
    values: {
      interaction_collective: {
        label: "J’aime échanger et agir avec les autres",
        icon: "🌱",
        enrichable: true,
        mission_card_tag: "Beaucoup d’échanges",
      },
      equilibre_collectif_autonomie: {
        label: "J’aime alterner les moments en groupe et en autonomie",
        icon: "🌿",
        enrichable: true,
        mission_card_tag: "Entre équipe et autonomie",
      },
      autonomie_principale: {
        label: "Je préfère avancer principalement en autonomie",
        icon: "🌳",
        enrichable: true,
        mission_card_tag: "Surtout en solo",
      },
      peu_importe: {
        label: "Peu importe",
        icon: "🤔",
        enrichable: false,
        neutral: true,
      },
    },
  },

  autonomie: {
    label: "Niveau d’autonomie et d’accompagnement",
    type: "categorical",
    enrichable: true,
    gate: false,
    values: {
      organisation_libre: {
        label: "On me donne un objectif et je m’organise librement",
        icon: "🤝",
        enrichable: true,
        mission_card_tag: "Organisation libre",
      },
      accompagnement_initial: {
        label: "J’aime être accompagné(e) au début, puis gagner en autonomie",
        icon: "🧭",
        enrichable: true,
        mission_card_tag: "Accompagnement au démarrage",
      },
      cadre_suivi_regulier: {
        label: "Je préfère avoir des consignes précises et un suivi régulier",
        icon: "🚀",
        enrichable: true,
        mission_card_tag: "Des consignes bien définies",
      },
      je_ne_sais_pas: {
        label: "Peu importe",
        icon: "🤔",
        enrichable: false,
        neutral: true,
      },
    },
  },

  // Historique (q2), plus posée depuis q3 ; à conserver pour la rétrocompatibilité.
  imprevu: {
    label: "Niveau d’imprévu",
    type: "categorical",
    enrichable: true,
    gate: false,
    values: {
      adaptation_rapide: {
        label: "J’aime quand il faut s’adapter rapidement",
        icon: "🚀",
        enrichable: true,
        mission_card_tag: "Ça bouge beaucoup",
      },
      imprevu_modere: {
        label: "Un peu d’imprévu, ça me va",
        icon: "💪",
        enrichable: true,
        mission_card_tag: "Des journées variées",
      },
      cadre_previsible: {
        label: "Je préfère savoir à quoi m’attendre",
        icon: "🧘",
        enrichable: true,
        mission_card_tag: "Un cadre stable",
      },
      je_ne_sais_pas: {
        label: "Je ne sais pas encore",
        icon: "🤔",
        enrichable: false,
        neutral: true,
      },
    },
  },

  // Taxonomie historique utilisée par les prompts d'enrichissement v1 à v4, mais plus par
  // le prochain parcours. À conserver sans modifier pour la rétrocompatibilité ; candidate
  // à une suppression lors d'une future migration.
  formation_onisep: {
    label: "Domaine de formation ONISEP",
    type: "multi_value",
    enrichable: true,
    gate: false,
    values: {
      environnement_nature_sciences: { label: "Environnement, nature et sciences", icon: "🌱", enrichable: true },
      numerique_communication: { label: "Numérique et communication", icon: "💻", enrichable: true },
      commerce_gestion_finance: { label: "Commerce, gestion, finance et services", icon: "💼", enrichable: true },
      societe_droit_politique: { label: "Société, droit et politique", icon: "⚖️", enrichable: true },
      education_culture_creation: { label: "Éducation, culture et création", icon: "🧑‍🏫", enrichable: true },
      social_sante_sport: { label: "Social, santé et sport", icon: "🌍", enrichable: true },
      technique_industrie_construction: { label: "Technique, industrie et construction", icon: "🛠️", enrichable: true },
      securite_defense_logistique: { label: "Sécurité, défense et logistique", icon: "🚓", enrichable: true },
      je_ne_sais_pas: { label: "Je ne sais pas encore", icon: "🤷", enrichable: false, neutral: true },
    },
  },

  motivation_recherche: {
    label: "Motivation de recherche",
    type: "multi_value",
    enrichable: true,
    gate: false,
    values: {
      premiere_experience: {
        label: "J'ai besoin d'une première expérience",
        icon: "🙏",
        enrichable: true,
        mission_card_tag: "Idéale pour débuter",
      },
      rencontres: {
        label: "J'aimerais rencontrer de nouvelles personnes",
        icon: "🤝",
        enrichable: true,
        mission_card_tag: "De nouvelles rencontres",
      },
      decouverte_metier: {
        label: "Je veux découvrir un métier",
        icon: "🧭",
        enrichable: true,
        mission_card_tag: "Pour découvrir un métier",
      },
      indemnisation: {
        label: "Je cherche une mission indemnisée",
        icon: "💸",
        enrichable: false,
        mission_card_tag: "Indemnisée",
      },
      agir_pour_une_cause: {
        label: "Je veux aider une cause qui me tient à cœur",
        icon: "🌍",
        enrichable: true,
        mission_card_tag: "Utile à une cause",
        // Portée par 99 % des missions : la cocher avantage tout le catalogue, donc personne.
        // Exclue du scoring (poids 0) tout en restant proposée dans le quiz et enrichie côté mission.
        neutral: true,
      },
      horaires_flexibles: {
        label: "Je veux avoir des horaires flexibles",
        icon: "📅",
        enrichable: true,
        mission_card_tag: "Horaires flexibles",
      },
      securite_pays: {
        label: "Je veux contribuer à la sécurité de mon pays",
        icon: "🪖",
        enrichable: true,
        mission_card_tag: "Au service de la sécurité de tous",
      },
      remote: {
        label: "Je veux pouvoir participer à distance",
        icon: "💻",
        enrichable: false,
        mission_card_tag: "À distance",
      },
      autre: {
        label: "Je ne sais pas encore",
        icon: "🤔",
        enrichable: false,
        neutral: true,
      },
    },
  },

  // ─── Taxonomies déclaratives côté quiz, non enrichies par LLM ─────────────

  // Historique (q1), plus posée depuis q2 ; à conserver pour la rétrocompatibilité.
  statut: {
    label: "Statut utilisateur",
    type: "categorical",
    enrichable: false,
    gate: false,
    values: {
      lyceen: { label: "Je suis au lycée", icon: "🧑‍", enrichable: false },
      etudiant: { label: "Je fais des études", icon: "🎓", enrichable: false },
      demandeur_emploi: { label: "Je recherche un emploi", icon: "🕵️‍♂️", enrichable: false },
      actif: { label: "J’ai une activité professionnelle", icon: "💼", enrichable: false },
      autre: { label: "Autre situation", icon: "🤷", enrichable: false },
    },
  },

  handicap: {
    label: "Situation de handicap",
    type: "categorical",
    enrichable: false,
    gate: false,
    values: {
      oui: { label: "Oui", icon: null, enrichable: false },
      non: { label: "Non", icon: null, enrichable: false },
      ne_se_prononce_pas: { label: "Je préfère ne pas répondre", icon: null, enrichable: false },
    },
  },

  // Mode de déplacement habituel. Sert à calibrer le rayon de recherche (`location.radius_km`)
  // côté quiz ; pas utilisée directement par le matching.
  mobilite: {
    label: "Mode de déplacement",
    type: "categorical",
    enrichable: false,
    gate: false,
    values: {
      pied_transports: { label: "À pied / en transports en commun", icon: "🚶", enrichable: false },
      velo: { label: "En vélo", icon: "☺️🚲", enrichable: false },
      voiture: { label: "En voiture", icon: "🚗", enrichable: false },
    },
  },

  // Taxonomie historique utilisée par les anciens parcours du quiz, mais plus par le
  // prochain parcours. À conserver sans modifier pour la rétrocompatibilité ; candidate
  // à une suppression lors d'une future migration.
  motivation: {
    label: "Motivation utilisateur",
    type: "categorical",
    enrichable: false,
    gate: false,
    values: {
      me_sentir_utile: {
        label: "Me sentir utile, rencontrer de nouvelles personnes",
        sublabel: "Être utile à des personnes ou à une cause",
        icon: "🙏🏻",
        enrichable: false,
      },
      booster_parcoursup: {
        label: "Booster mon dossier Parcoursup",
        sublabel: "Apprendre des compétences",
        icon: "🎓",
        enrichable: false,
      },
      tester_orientation: {
        label: "Tester une orientation",
        sublabel: "Explorer un secteur ou un métier",
        icon: "🧭",
        enrichable: false,
      },
      servir_le_pays: {
        label: "Servir le pays",
        sublabel: "Participer à des missions d'intérêt général",
        icon: "🇫🇷",
        enrichable: false,
      },
      ne_sais_pas: {
        label: "Je ne sais pas encore",
        sublabel: "Je déciderai en découvrant les missions",
        icon: "🤔‍",
        enrichable: false,
      },
      booster_cv: {
        label: "Booster mon CV",
        sublabel: "Acquérir des compétences en rapport avec mes études",
        icon: "📄",
        enrichable: false,
      },
      decouvrir_domaine: {
        label: "Découvrir un nouveau domaine",
        sublabel: "Pour me ré-orienter, avoir une expérience pour tester...",
        icon: "🧭",
        enrichable: false,
      },
      experience_terrain: {
        label: "Avoir une 1ère expérience terrain",
        icon: "🌱",
        sublabel: "Me lancer concrètement dans le monde professionnel",
        enrichable: false,
      },
      partir_etranger: {
        label: "Partir à l'étranger",
        sublabel: "Vivre une expérience d'engagement dans un autre pays",
        icon: "🌍",
        enrichable: false,
      },
      competences_interet_general: {
        label: "Utiliser mes compétences pour l'intérêt général",
        icon: "💡",
        sublabel: "Mettre mon savoir-faire au service de la collectivité",
        enrichable: false,
      },
      reprendre_confiance: {
        label: "Reprendre confiance en moi",
        icon: "💪",
        sublabel: "Développer mes capacités et mon estime de moi",
        enrichable: false,
      },
      reprendre_activite: {
        label: "Garder / reprendre une activité",
        icon: "🔄",
        sublabel: "Rester actif·ve et maintenir un rythme",
        enrichable: false,
      },
      enrichir_cv: {
        label: "Enrichir mon CV",
        sublabel: "Acquérir des compétences en rapport avec mon métier",
        icon: "📄",
        enrichable: false,
      },
      preparer_reconversion: {
        label: "Préparer une reconversion professionnelle",
        sublabel: "Tester un nouveau domaine / métier",
        icon: "🧭",
        enrichable: false,
      },
    },
  },

  location: {
    label: "Localisation",
    type: "value",
    enrichable: false,
    gate: false,
    transformer: resolveLocationValues,
    values: {},
  },

  departmentCode: {
    label: "Département",
    type: "multi_value",
    enrichable: false,
    gate: false,
    transformer: resolveDepartmentCodeValues,
    values: DEPARTMENT_CODE_VALUES,
  },

  // ─── taxonomy gate (filtre dur dans le matching) ────────────────────────

  tranche_age: {
    label: "Tranche d'âge",
    type: "gate",
    enrichable: false, // pas enrichie par le LLM — calculée côté client (âge saisi)
    gate: true,
    transformer: resolveTrancheAgeValues,
    values: {
      moins_18_ans: { label: "Moins de 18 ans", icon: null, enrichable: false },
      entre_18_25_ans: { label: "18-25 ans", icon: null, enrichable: false },
      entre_25_30_ans: { label: "26-30 ans", icon: null, enrichable: false },
      entre_30_45_ans: { label: "31-45 ans", icon: null, enrichable: false },
      entre_46_67_ans: { label: "46-67 ans", icon: null, enrichable: false },
      entre_68_72_ans: { label: "68-72 ans", icon: null, enrichable: false },
      plus_72_ans: { label: "73 ans et plus", icon: null, enrichable: false },
      entre_17_18_ans: { label: "17-18 ans", icon: null, enrichable: false, hidden: true },
      entre_46_66_ans: { label: "46-66 ans", icon: null, enrichable: false, hidden: true },
      moins_31_ans_handicap: { label: "Moins de 31 ans — situation de handicap", icon: null, enrichable: false, hidden: true },
    },
  },
} as const;
